import type { Component, Reactive } from 'vue'
import type { ComponentProps } from 'vue-component-type-helpers'
import { createSharedComposable } from '@vueuse/core'

/**
 * Programmatic toast system. Vendored into the main app alongside
 * {@link useDialog} so it no longer depends on the `layers/ui` submodule.
 * Provider is set up by `app/plugins/toast.ts`.
 */
interface _item<T = Component> {
  props: ComponentProps<T>
  component: T
  remove: () => void
}

export const toastInjectionKey: InjectionKey<Reactive<Map<symbol, _item>>> = Symbol('trakle.toast')

export const useToast = createSharedComposable(<T extends Component>(component: T) => {
  type _props = ComponentProps<T>
  const toasts = inject(toastInjectionKey)
  if (!toasts) throw new Error('useToast() is called without provider')
  component = markRaw(component)

  return function open(props: _props) {
    const symbol = Symbol('Toast')
    function remove() {
      toasts?.delete(symbol)
    }

    toasts.set(symbol, { props, component, remove })
    return function patch(props: _props) {
      toasts.set(symbol, { props, component, remove })
    }
  }
})
